/*
 * Author: Valeria Sinnati
 *
 *   |\---/|
 *   | ,_, |   Admin pipeline view for scheduled videos.
 *    \_`_/-..----.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getScheduledVideos, getScheduledVideosStats, getScheduledVideoDetail, deleteScheduledVideo, retryScheduledVideoPublish, bulkDeleteScheduledVideos, bulkRetryScheduledVideos, rescheduleScheduledPost, retryAudioScheduledVideo, regenerateTextScheduledVideo } from '../../services/adminApi';
import { notify } from '../../utils/notify';
import './ScheduledVideos.css';

const STATUS_CONFIG = {
  draft:              { className: 'sv-status-draft',      icon: '📝' },
  generating_audio:   { className: 'sv-status-pipeline',   icon: '🔊' },
  audio_failed:       { className: 'sv-status-failed',     icon: '❌' },
  generating_video:   { className: 'sv-status-pipeline',   icon: '🎬' },
  video_failed:       { className: 'sv-status-failed',     icon: '❌' },
  pending_validation: { className: 'sv-status-pending',    icon: '⏳' },
  approved:           { className: 'sv-status-approved',   icon: '✅' },
  queued:             { className: 'sv-status-queued',     icon: '📋' },
  publishing:         { className: 'sv-status-pipeline',   icon: '📤' },
  published:          { className: 'sv-status-published',  icon: '🟢' },
  partially_published:{ className: 'sv-status-partial',    icon: '🟡' },
  failed:             { className: 'sv-status-failed',     icon: '🔴' },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const AUTO_POLL_INTERVAL = 15000; // 15 seconds

const RETRYABLE_STATUSES = ['failed', 'partially_published', 'video_failed'];
const isRetryable = (status) => RETRYABLE_STATUSES.includes(status);

// Statuses from which an admin/superadmin can force a full audio+video
// regeneration (retry-audio): error states plus posts still in the queue.
// Mirror ``RETRY_AUDIO_ALLOWED_STATUSES`` in
// backend/app/api/v1/admin_videos.py — keep the two lists in sync.
const REGENERATABLE_STATUSES = [
  'draft', 'audio_failed', 'video_failed', 'failed',
  'pending_validation', 'approved', 'queued',
];
const isRegeneratable = (status) => REGENERATABLE_STATUSES.includes(status);

// A video that already went out (fully OR partially) must NOT be
// regenerated — re-running the pipeline would re-publish and duplicate
// the post on the platform(s) where it already succeeded. The button is
// shown but disabled for these statuses.
//  ╱|、
// (˚ˎ。7   published / partially_published -> regenerate blocked
//  |、˜〵
const PUBLISHED_STATUSES = ['published', 'partially_published'];
const isPublished = (status) => PUBLISHED_STATUSES.includes(status);

// Statuses for which a superadmin can shift ``scheduled_publish_at``.
// Must mirror ``_RESCHEDULABLE_STATUSES`` in ``backend/app/api/v1/admin.py``
// — keep these two lists in sync.
const RESCHEDULABLE_STATUSES = ['draft', 'pending_validation', 'approved', 'queued'];
const isReschedulable = (status) => RESCHEDULABLE_STATUSES.includes(status);

// Format a Date into the value expected by ``<input type="datetime-local">``
// (``YYYY-MM-DDTHH:mm``) in the user's *local* timezone — the browser then
// re-interprets the value as local time when we convert back to a Date.
const toLocalDateTimeInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

function ScheduledVideos() {
  const { t } = useTranslation();

  // Data
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState(''); // raw text in the field
  const [search, setSearch] = useState('');           // debounced value sent to the API
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Show only posts whose batch is stuck (should have finalized but didn't).
  const [onlyStuck, setOnlyStuck] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retryConfirm, setRetryConfirm] = useState(null); // post object to retry
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  // Reschedule modal (superadmin only).
  const { user: currentUser } = useSelector((state) => state.auth);
  const isSuperadmin = currentUser?.role === 'superadmin';
  // Admin OR superadmin — both may regenerate audio+video (retry-audio).
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // post being rescheduled
  const [rescheduleDateTime, setRescheduleDateTime] = useState(''); // local datetime-input value
  const [rescheduling, setRescheduling] = useState(false);

  // Audio-failed recovery (superadmin only): { post, type: 'audio' | 'text' }.
  // 'audio' re-runs the audio→video pipeline; 'text' regenerates the script.
  const [recoveryTarget, setRecoveryTarget] = useState(null);
  const [recoveryRunning, setRecoveryRunning] = useState(false);

  const pollRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getScheduledVideosStats();
      setStats(res.data);

      // Auto-enable polling if pipeline is active
      const pipelineActive = (res.data.pipeline_in_progress || 0) > 0;
      setAutoRefresh(pipelineActive);
    } catch (err) {
      console.error('Failed to load video stats:', err);
    }
  }, []);

  const fetchPosts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await getScheduledVideos(
        page, pageSize,
        statusFilter || null,
        null, null,
        dateFrom || null,
        dateTo || null,
        sortBy, sortOrder,
        search || null,
        onlyStuck,
      );
      setPosts(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load scheduled videos:', err);
      if (!silent) setError(t('admin.scheduledVideos.fetchError'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, statusFilter, dateFrom, dateTo, sortBy, sortOrder, search, onlyStuck, t]);

  // Initial load
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // Debounce the free-text search (email / social handle) so we don't
  // fire a request on every keystroke; reset to page 1 on each change.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Auto-poll when pipeline is active
  useEffect(() => {
    if (autoRefresh) {
      pollRef.current = setInterval(() => {
        fetchPosts(true);
        fetchStats();
      }, AUTO_POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [autoRefresh, fetchPosts, fetchStats]);

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  // Toggle the "stuck batches only" filter (driven by the stat card).
  const toggleStuckFilter = () => {
    setOnlyStuck((v) => !v);
    setPage(1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const openDetail = async (postId) => {
    setDetailLoading(true);
    try {
      const res = await getScheduledVideoDetail(postId);
      setSelectedPost(res.data);
    } catch (err) {
      console.error('Failed to load post detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedPost(null);
    setDeleteConfirm(false);
  };

  const handleDeletePost = async (postId) => {
    setDeleting(true);
    try {
      await deleteScheduledVideo(postId);
      closeDetail();
      fetchPosts();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete post:', err);
      notify.error(err.response?.data?.detail || t('admin.scheduledVideos.deleteError'));
    } finally {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  // ── Retry Publish handlers ──
  const handleRetryClick = (post, e) => {
    if (e) e.stopPropagation();
    setRetryConfirm(post);
    setRetryResult(null);
  };

  const cancelRetry = () => {
    setRetryConfirm(null);
    setRetryResult(null);
  };

  const confirmRetryPublish = async () => {
    if (!retryConfirm) return;
    const postId = retryConfirm.id;
    setRetrying(true);
    setRetryResult(null);

    try {
      const res = await retryScheduledVideoPublish(postId);
      setRetryResult({
        type: 'success',
        message: res.data.message || t('admin.scheduledVideos.retrySuccess'),
      });
      setRetryConfirm(null);
      // Refresh after a short delay so the queued status shows up
      setTimeout(() => {
        fetchPosts(true);
        fetchStats();
      }, 2000);
    } catch (err) {
      setRetryResult({
        type: 'error',
        message: err.response?.data?.detail || t('admin.scheduledVideos.retryError'),
      });
    } finally {
      setRetrying(false);
    }
  };

  // ── Reschedule handlers (superadmin only) ──
  // ``rescheduleDateTime`` is held in the user's LOCAL timezone (the
  // format ``<input type="datetime-local">`` works with). On submit we
  // build a Date from it — JS interprets the value as local time — and
  // send the ISO string (which carries the local offset) to the backend.
  const openReschedule = (post, e) => {
    if (e) e.stopPropagation();
    const current = post.scheduled_publish_at ? new Date(post.scheduled_publish_at) : new Date();
    setRescheduleDateTime(toLocalDateTimeInputValue(current));
    setRescheduleTarget(post);
  };

  const closeReschedule = () => {
    if (rescheduling) return;
    setRescheduleTarget(null);
    setRescheduleDateTime('');
  };

  const submitReschedule = async () => {
    if (!rescheduleTarget || !rescheduleDateTime) return;
    const localDate = new Date(rescheduleDateTime);
    if (Number.isNaN(localDate.getTime())) {
      setRetryResult({
        type: 'error',
        message: t('admin.scheduledVideos.reschedule.invalidDate', 'Data non valida'),
      });
      return;
    }
    if (localDate.getTime() <= Date.now()) {
      setRetryResult({
        type: 'error',
        message: t(
          'admin.scheduledVideos.reschedule.mustBeFuture',
          'La nuova data deve essere nel futuro'
        ),
      });
      return;
    }
    const postId = rescheduleTarget.id;
    setRescheduling(true);
    try {
      const res = await rescheduleScheduledPost(postId, localDate.toISOString());
      const newIso = res.data?.scheduled_publish_at;
      setRetryResult({
        type: 'success',
        message: t('admin.scheduledVideos.reschedule.success', {
          defaultValue: 'Post #{{id}} riprogrammato',
          id: postId,
        }),
      });
      // Reflect the change locally without a full reload.
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, scheduled_publish_at: newIso } : p
        )
      );
      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost({ ...selectedPost, scheduled_publish_at: newIso });
      }
      setRescheduleTarget(null);
      setRescheduleDateTime('');
      fetchStats();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setRetryResult({
        type: 'error',
        message:
          detail || t('admin.scheduledVideos.reschedule.error', 'Riprogrammazione fallita'),
      });
    } finally {
      setRescheduling(false);
    }
  };

  // ── Recovery handlers ──
  // 'audio' = regenerate audio+video (admin & superadmin, any recoverable/
  // queued status); 'text' = regenerate the script (superadmin, audio_failed).
  const openRecovery = (post, type, e) => {
    if (e) e.stopPropagation();
    setRecoveryTarget({ post, type });
    setRetryResult(null);
  };

  const cancelRecovery = () => {
    if (recoveryRunning) return;
    setRecoveryTarget(null);
  };

  const confirmRecovery = async () => {
    if (!recoveryTarget) return;
    const { post, type } = recoveryTarget;
    setRecoveryRunning(true);
    try {
      const res =
        type === 'audio'
          ? await retryAudioScheduledVideo(post.id)
          : await regenerateTextScheduledVideo(post.id);
      setRetryResult({
        type: 'success',
        message:
          res.data?.message ||
          (type === 'audio'
            ? t('admin.scheduledVideos.audioRecovery.retryAudioSuccess', 'Rigenerazione audio+video avviata')
            : t('admin.scheduledVideos.audioRecovery.regenTextSuccess', 'Rigenerazione testo avviata')),
      });
      setRecoveryTarget(null);
      // The post changes status (-> generating/regenerating) — close the
      // now-stale detail modal.
      if (selectedPost && selectedPost.id === post.id) closeDetail();
      // Refresh after a short delay so the new status shows up.
      setTimeout(() => {
        fetchPosts(true);
        fetchStats();
      }, 2000);
    } catch (err) {
      setRetryResult({
        type: 'error',
        message:
          err.response?.data?.detail ||
          t('admin.scheduledVideos.audioRecovery.error', 'Operazione fallita'),
      });
    } finally {
      setRecoveryRunning(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const renderStatusBadge = (statusVal) => {
    const cfg = STATUS_CONFIG[statusVal] || { className: 'sv-status-draft', icon: '❓' };
    const label = t(`admin.scheduledVideos.status_${statusVal}`, statusVal);
    return (
      <span className={`sv-badge ${cfg.className}`}>
        <span className="sv-badge-icon">{cfg.icon}</span> {label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const sortIndicator = (field) => {
    if (sortBy !== field) return '';
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="scheduled-videos-page">
      <div className="sv-header">
        <div className="sv-header-left">
          <h1>{t('admin.scheduledVideos.title')}</h1>
          {autoRefresh && (
            <span className="sv-live-indicator" title={t('admin.scheduledVideos.autoRefreshActive')}>
              <span className="sv-live-dot" /> {t('admin.scheduledVideos.live')}
            </span>
          )}
        </div>
        <button className="sv-btn-refresh" onClick={() => { fetchStats(); fetchPosts(); }}>
          ↻ {t('admin.scheduledVideos.refresh')}
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="sv-stats-row">
          <div className="sv-stat-card">
            <span className="sv-stat-value">{stats.total_videos}</span>
            <span className="sv-stat-label">{t('admin.scheduledVideos.statTotal')}</span>
          </div>
          <div className="sv-stat-card sv-stat-published">
            <span className="sv-stat-value">{stats.status_counts?.published || 0}</span>
            <span className="sv-stat-label">{t('admin.scheduledVideos.statPublished')}</span>
          </div>
          <div className="sv-stat-card sv-stat-pipeline">
            <span className="sv-stat-value">{stats.pipeline_in_progress}</span>
            <span className="sv-stat-label">{t('admin.scheduledVideos.statPipeline')}</span>
          </div>
          <div className="sv-stat-card sv-stat-failed">
            <span className="sv-stat-value">{stats.failed_count}</span>
            <span className="sv-stat-label">{t('admin.scheduledVideos.statFailed')}</span>
          </div>
          {/* Stuck batches — clickable: toggles the "only stuck" filter so an
              admin can immediately list the affected posts. */}
          <div
            className="sv-stat-card sv-stat-failed"
            onClick={toggleStuckFilter}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') toggleStuckFilter(); }}
            title={t('admin.scheduledVideos.statStuckHint', 'Batch che dovrebbero essere completati ma sono bloccati in generating — clicca per filtrare')}
            style={{ cursor: 'pointer', outline: onlyStuck ? '2px solid #e53e3e' : 'none' }}
          >
            <span className="sv-stat-value">{stats.stuck_batches || 0}</span>
            <span className="sv-stat-label">
              {onlyStuck ? '✕ ' : ''}{t('admin.scheduledVideos.statStuck', 'Batch bloccati')}
            </span>
          </div>
          <div className="sv-stat-card sv-stat-today">
            <span className="sv-stat-value">{stats.published_today}</span>
            <span className="sv-stat-label">{t('admin.scheduledVideos.statPublishedToday')}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="sv-filters">
        <input
          type="text"
          className="sv-search-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('admin.scheduledVideos.searchPlaceholder', 'Cerca per nome, email o social account…')}
          aria-label={t('admin.scheduledVideos.searchPlaceholder', 'Cerca per nome, email o social account…')}
        />
        <select
          className="sv-select"
          value={statusFilter}
          onChange={handleStatusFilterChange}
        >
          <option value="">{t('admin.scheduledVideos.filterAllStatuses')}</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`admin.scheduledVideos.status_${s}`, s)}</option>
          ))}
        </select>
        <div className="sv-date-filters">
          <label className="sv-date-label">
            {t('admin.scheduledVideos.dateFrom')}
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
          </label>
          <label className="sv-date-label">
            {t('admin.scheduledVideos.dateTo')}
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
          </label>
        </div>
      </div>

      {/* Error */}
      {error && <div className="sv-error">{error}</div>}

      {/* Table */}
      {loading ? (
        <div className="sv-loading">{t('common.loading')}</div>
      ) : posts.length === 0 ? (
        <div className="sv-empty">{t('admin.scheduledVideos.noPosts')}</div>
      ) : (
        <div className="sv-table-container">
          <table className="sv-table" role="grid">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('admin.scheduledVideos.colUser')}</th>
                <th>{t('admin.scheduledVideos.colStatus')}</th>
                <th>{t('admin.scheduledVideos.colScript')}</th>
                <th
                  className="sv-th-sortable"
                  onClick={() => toggleSort('scheduled_date')}
                  role="button"
                  tabIndex={0}
                >
                  {t('admin.scheduledVideos.colScheduledDate')}{sortIndicator('scheduled_date')}
                </th>
                <th>{t('admin.scheduledVideos.colPublishTime')}</th>
                <th>{t('admin.scheduledVideos.colPublishedAt')}</th>
                <th>Platforms</th>
                <th>{t('admin.scheduledVideos.colVideo')}</th>
                <th
                  className="sv-th-sortable"
                  onClick={() => toggleSort('created_at')}
                  role="button"
                  tabIndex={0}
                >
                  {t('admin.scheduledVideos.colCreated')}{sortIndicator('created_at')}
                </th>
                <th>{t('admin.scheduledVideos.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr
                  key={post.id}
                  className="sv-row-clickable"
                  onClick={() => openDetail(post.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') openDetail(post.id); }}
                >
                  <td className="sv-cell-id">#{post.id}</td>
                  <td>
                    <div className="sv-user-cell">
                      <span className="sv-user-email">{post.user_email || '—'}</span>
                      {post.instagram_username && (
                        <span className="sv-ig-username">@{post.instagram_username}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {renderStatusBadge(post.status)}
                    {post.batch_stuck && (
                      <span
                        className="sv-badge sv-status-failed"
                        title={t('admin.scheduledVideos.batchStuckHint', 'Batch bloccato: tutti i post sono pronti ma il batch è rimasto in generating')}
                        style={{ marginTop: 4 }}
                      >
                        ⚠️ {t('admin.scheduledVideos.batchStuck', 'batch bloccato')}
                      </span>
                    )}
                  </td>
                  <td className="sv-cell-script">{post.script_text || '—'}</td>
                  <td>{formatDate(post.scheduled_date)}</td>
                  <td>{formatDateTime(post.scheduled_publish_at)}</td>
                  <td>{formatDateTime(post.published_at)}</td>
                  <td>
                    <div className="sv-platforms-cell" onClick={(e) => e.stopPropagation()}>
                      {(post.target_platforms || ['instagram']).map((p) => {
                        const icon = p === 'instagram' ? '📸' : p === 'tiktok' ? '🎵' : '📘';
                        const url = p === 'instagram' ? post.instagram_permalink : p === 'tiktok' ? post.tiktok_share_url : p === 'facebook' ? post.facebook_permalink : null;
                        const result = post.platform_results?.[p];
                        const statusClass = result === 'published' ? 'sv-platform-ok' : result ? 'sv-platform-fail' : '';
                        return url ? (
                          <a key={p} href={url} target="_blank" rel="noopener noreferrer" className={`sv-platform-icon ${statusClass}`} title={`${p}: ${result || 'pending'}`}>{icon}</a>
                        ) : (
                          <span key={p} className={`sv-platform-icon ${statusClass}`} title={`${p}: ${result || 'pending'}`}>{icon}</span>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    {post.video_url ? (
                      <span className="sv-has-video" title={t('admin.scheduledVideos.hasVideo')}>🎥</span>
                    ) : (
                      <span className="sv-no-video">—</span>
                    )}
                  </td>
                  <td>{formatDate(post.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {isRetryable(post.status) && post.video_url && (
                      <button
                        className="sv-btn-retry"
                        onClick={(e) => handleRetryClick(post, e)}
                        disabled={retrying}
                        title={t('admin.scheduledVideos.retryBtn')}
                      >
                        🔄 {t('admin.scheduledVideos.retryBtn')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="sv-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t('common.previous')}
          </button>
          <span className="sv-page-info">{page} {t('common.of')} {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {(selectedPost || detailLoading) && (
        <div className="sv-modal-overlay" onClick={closeDetail}>
          <div className="sv-modal-card" onClick={(e) => e.stopPropagation()}>
            {detailLoading ? (
              <div className="sv-loading">{t('common.loading')}</div>
            ) : selectedPost && (
              <>
                <div className="sv-modal-header">
                  <h2>{t('admin.scheduledVideos.detailTitle')} #{selectedPost.id}</h2>
                  <div className="sv-modal-header-actions">
                    {/* Retry button in detail modal */}
                    {isRetryable(selectedPost.status) && selectedPost.video_url && (
                      <button
                        className="sv-btn-retry"
                        onClick={() => handleRetryClick(selectedPost)}
                        disabled={retrying}
                      >
                        🔄 {t('admin.scheduledVideos.retryBtn')}
                      </button>
                    )}
                    {/* Reschedule button — superadmin only, only when the
                        post is still in a pre-publish state. */}
                    {isSuperadmin && isReschedulable(selectedPost.status) && (
                      <button
                        className="sv-btn-reschedule"
                        onClick={() => openReschedule(selectedPost)}
                        disabled={rescheduling}
                        title={t('admin.scheduledVideos.reschedule.btn', 'Riprogramma')}
                      >
                        📅 {t('admin.scheduledVideos.reschedule.btn', 'Riprogramma')}
                      </button>
                    )}
                    {/* Regenerate Audio+Video — admin & superadmin.
                        ``retry-audio`` rebuilds audio AND video; available for
                        error states and for videos still in the queue. Shown
                        but DISABLED once the post has been published — even
                        partially — so it can't be re-published / duplicated. */}
                    {isAdmin && (isRegeneratable(selectedPost.status) || isPublished(selectedPost.status)) && (
                      <button
                        className="sv-btn-retry"
                        onClick={() => openRecovery(selectedPost, 'audio')}
                        disabled={recoveryRunning || isPublished(selectedPost.status)}
                        title={
                          isPublished(selectedPost.status)
                            ? t('admin.scheduledVideos.audioRecovery.alreadyPublished', 'Il video è già stato pubblicato (anche parzialmente): non può essere rigenerato')
                            : t('admin.scheduledVideos.audioRecovery.retryAudioBtn', 'Rigenera Audio+Video')
                        }
                      >
                        🔄 {t('admin.scheduledVideos.audioRecovery.retryAudioBtn', 'Rigenera Audio+Video')}
                      </button>
                    )}
                    {/* Regenerate script text — superadmin only, audio_failed
                        only (rewrites the LLM script; unchanged behavior). */}
                    {isSuperadmin && selectedPost.status === 'audio_failed' && (
                      <button
                        className="sv-btn-reschedule"
                        onClick={() => openRecovery(selectedPost, 'text')}
                        disabled={recoveryRunning}
                        title={t('admin.scheduledVideos.audioRecovery.regenTextBtn', 'Rigenera Testo')}
                      >
                        ✏️ {t('admin.scheduledVideos.audioRecovery.regenTextBtn', 'Rigenera Testo')}
                      </button>
                    )}
                    {selectedPost.status !== 'publishing' && (
                      !deleteConfirm ? (
                        <button
                          className="sv-btn-delete"
                          onClick={() => setDeleteConfirm(true)}
                          title={t('admin.scheduledVideos.deletePost')}
                        >
                          🗑️ {t('admin.scheduledVideos.deletePost')}
                        </button>
                      ) : (
                        <div className="sv-delete-confirm">
                          <span className="sv-delete-confirm-text">
                            {t('admin.scheduledVideos.confirmDelete')}
                          </span>
                          <button
                            className="sv-btn-delete-yes"
                            onClick={() => handleDeletePost(selectedPost.id)}
                            disabled={deleting}
                          >
                            {deleting ? '...' : t('common.yes')}
                          </button>
                          <button
                            className="sv-btn-delete-no"
                            onClick={() => setDeleteConfirm(false)}
                          >
                            {t('common.no')}
                          </button>
                        </div>
                      )
                    )}
                    <button className="sv-modal-close" onClick={closeDetail}>✕</button>
                  </div>
                </div>
                <div className="sv-modal-body">
                  {/* Status & meta */}
                  <div className="sv-detail-status-row">
                    {renderStatusBadge(selectedPost.status)}
                    {selectedPost.batch_stuck && (
                      <span
                        className="sv-badge sv-status-failed"
                        title={t('admin.scheduledVideos.batchStuckHint', 'Batch bloccato: tutti i post sono pronti ma il batch è rimasto in generating')}
                      >
                        ⚠️ {t('admin.scheduledVideos.batchStuck', 'batch bloccato')}
                      </span>
                    )}
                    {selectedPost.status_message && (
                      <span className="sv-detail-status-msg">{selectedPost.status_message}</span>
                    )}
                  </div>

                  <div className="sv-detail-grid">
                    <div className="sv-detail-item">
                      <label>{t('admin.scheduledVideos.colUser')}</label>
                      <span>{selectedPost.user_email || '—'}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>Instagram</label>
                      <span>{selectedPost.instagram_username ? `@${selectedPost.instagram_username}` : '—'}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>TikTok</label>
                      <span>{selectedPost.tiktok_display_name ? `@${selectedPost.tiktok_display_name}` : '—'}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>Target Platforms</label>
                      <span>{(selectedPost.target_platforms || ['instagram']).map(p => p === 'instagram' ? '📸' : p === 'tiktok' ? '🎵' : '📘').join(' ')}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>Batch ID</label>
                      <span>{selectedPost.batch_id}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>{t('admin.scheduledVideos.colDay')}</label>
                      <span>{selectedPost.day_name}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>{t('admin.scheduledVideos.colScheduledDate')}</label>
                      <span>{selectedPost.scheduled_date || '—'}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>{t('admin.scheduledVideos.colPublishTime')}</label>
                      <span>{formatDateTime(selectedPost.scheduled_publish_at)}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>{t('admin.scheduledVideos.colPublishedAt')}</label>
                      <span>{formatDateTime(selectedPost.published_at)}</span>
                    </div>
                    <div className="sv-detail-item">
                      <label>{t('admin.scheduledVideos.retryCount')}</label>
                      <span>{selectedPost.retry_count}</span>
                    </div>
                  </div>

                  {/* Script */}
                  <div className="sv-detail-section">
                    <h4>{t('admin.scheduledVideos.detailScript')}</h4>
                    <div className="sv-detail-text-box">{selectedPost.script_text || '—'}</div>
                  </div>

                  {/* Caption */}
                  <div className="sv-detail-section">
                    <h4>{t('admin.scheduledVideos.detailCaption')}</h4>
                    <div className="sv-detail-text-box">{selectedPost.caption || '—'}</div>
                  </div>

                  {/* Hashtags */}
                  {selectedPost.hashtags && selectedPost.hashtags.length > 0 && (
                    <div className="sv-detail-section">
                      <h4>{t('admin.scheduledVideos.detailHashtags')}</h4>
                      <div className="sv-hashtags">
                        {selectedPost.hashtags.map((tag, i) => (
                          <span key={i} className="sv-hashtag">#{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Video player */}
                  {selectedPost.video_url && (
                    <div className="sv-detail-section">
                      <h4>{t('admin.scheduledVideos.detailVideo')}</h4>
                      <div className="sv-video-container">
                        <video
                          controls
                          preload="metadata"
                          className="sv-video-player"
                          src={selectedPost.video_url}
                        >
                          {t('admin.scheduledVideos.videoNotSupported')}
                        </video>
                      </div>
                      {selectedPost.video_duration && (
                        <p className="sv-video-duration">
                          {t('admin.scheduledVideos.duration')}: {selectedPost.video_duration.toFixed(1)}s
                        </p>
                      )}
                    </div>
                  )}

                  {/* Permalinks */}
                  {selectedPost.instagram_permalink && (
                    <div className="sv-detail-section">
                      <h4>📸 Instagram</h4>
                      <a
                        href={selectedPost.instagram_permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sv-permalink"
                      >
                        {t('admin.scheduledVideos.viewOnInstagram')} ↗
                      </a>
                    </div>
                  )}
                  {selectedPost.tiktok_share_url && (
                    <div className="sv-detail-section">
                      <h4>🎵 TikTok</h4>
                      <a
                        href={selectedPost.tiktok_share_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sv-permalink"
                      >
                        {t('admin.scheduledVideos.viewOnTikTok', 'View on TikTok')} ↗
                      </a>
                    </div>
                  )}
                  {selectedPost.facebook_permalink && (
                    <div className="sv-detail-section">
                      <h4>📘 Facebook</h4>
                      <a
                        href={selectedPost.facebook_permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sv-permalink"
                      >
                        {t('admin.scheduledVideos.viewOnFacebook', 'View on Facebook')} ↗
                      </a>
                    </div>
                  )}

                  {/* Platform results JSON */}
                  {selectedPost.platform_results && (
                    <div className="sv-detail-section">
                      <h4>{t('admin.scheduledVideos.detailPlatformResults')}</h4>
                      <pre className="sv-json-box">
                        {JSON.stringify(selectedPost.platform_results, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Retry Confirm Modal */}
      {retryConfirm && (
        <div className="sv-modal-overlay" onClick={cancelRetry}>
          <div className="sv-retry-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🔄 {t('admin.scheduledVideos.retryTitle')}</h3>

            <div className="sv-retry-detail">
              <p><strong>Post #{retryConfirm.id}</strong> — {retryConfirm.user_email || '—'}</p>

              {/* Show target platforms */}
              <div className="sv-retry-platforms">
                <span className="sv-retry-label">{t('admin.scheduledVideos.retryPlatforms')}:</span>
                {(retryConfirm.target_platforms || ['instagram']).map((p) => (
                  <span key={p} className="sv-retry-platform-tag">
                    {p === 'instagram' ? '📸' : p === 'tiktok' ? '🎵' : '📘'} {p}
                  </span>
                ))}
              </div>

              {/* Show previous error */}
              {retryConfirm.status_message && (
                <div className="sv-retry-error-box">
                  <span className="sv-retry-label">{t('admin.scheduledVideos.previousError')}:</span>
                  <p className="sv-retry-error-text">{retryConfirm.status_message}</p>
                </div>
              )}

              {/* Show per-platform results if available */}
              {retryConfirm.platform_results && Object.keys(retryConfirm.platform_results).length > 0 && (
                <div className="sv-retry-platform-results">
                  <span className="sv-retry-label">{t('admin.scheduledVideos.platformResults')}:</span>
                  {Object.entries(retryConfirm.platform_results).map(([platform, result]) => (
                    <div key={platform} className="sv-retry-result-row">
                      <span>{platform === 'instagram' ? '📸' : platform === 'tiktok' ? '🎵' : '📘'} {platform}</span>
                      <span className={result === 'published' ? 'sv-result-ok' : 'sv-result-fail'}>
                        {result}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="sv-retry-warning">⚠️ {t('admin.scheduledVideos.retryWarning')}</p>

            <div className="sv-retry-actions">
              <button
                className="sv-btn-retry-confirm"
                onClick={confirmRetryPublish}
                disabled={retrying}
              >
                {retrying ? '⏳ ...' : `🔄 ${t('admin.scheduledVideos.retryNow')}`}
              </button>
              <button className="sv-btn-retry-cancel" onClick={cancelRetry}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retry Result Toast */}
      {retryResult && (
        <div className={`sv-retry-toast sv-retry-toast-${retryResult.type}`}>
          <span>{retryResult.type === 'success' ? '✅' : '❌'} {retryResult.message}</span>
          <button className="sv-retry-toast-close" onClick={() => setRetryResult(null)}>✕</button>
        </div>
      )}

      {/* Reschedule Modal (superadmin only) */}
      {rescheduleTarget && (
        <div className="sv-modal-overlay" onClick={closeReschedule}>
          <div className="sv-reschedule-modal" onClick={(e) => e.stopPropagation()}>
            <h3>📅 {t('admin.scheduledVideos.reschedule.title', 'Riprogramma pubblicazione')}</h3>
            <div className="sv-reschedule-info">
              <p>
                <strong>Post #{rescheduleTarget.id}</strong>
                {rescheduleTarget.user_email ? ` — ${rescheduleTarget.user_email}` : ''}
              </p>
              <p className="sv-reschedule-current">
                {t('admin.scheduledVideos.reschedule.currentLabel', 'Attuale')}:{' '}
                <strong>
                  {rescheduleTarget.scheduled_publish_at
                    ? new Date(rescheduleTarget.scheduled_publish_at).toLocaleString()
                    : t('admin.scheduledVideos.reschedule.notSet', 'non impostato')}
                </strong>
              </p>
            </div>
            <label className="sv-reschedule-label">
              {t('admin.scheduledVideos.reschedule.newLabel', 'Nuova data e ora')}
              <input
                type="datetime-local"
                className="sv-reschedule-input"
                value={rescheduleDateTime}
                onChange={(e) => setRescheduleDateTime(e.target.value)}
                min={toLocalDateTimeInputValue(new Date(Date.now() + 60000))}
                disabled={rescheduling}
              />
            </label>
            <div className="sv-reschedule-actions">
              <button
                className="sv-btn-cancel"
                onClick={closeReschedule}
                disabled={rescheduling}
              >
                {t('common.cancel')}
              </button>
              <button
                className="sv-btn-reschedule-confirm"
                onClick={submitReschedule}
                disabled={rescheduling || !rescheduleDateTime}
              >
                {rescheduling
                  ? '…'
                  : t('admin.scheduledVideos.reschedule.confirm', 'Riprogramma')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio-failed recovery confirm modal (superadmin only) */}
      {recoveryTarget && (
        <div className="sv-modal-overlay" onClick={cancelRecovery}>
          <div className="sv-retry-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {recoveryTarget.type === 'audio' ? '🔄 ' : '✏️ '}
              {recoveryTarget.type === 'audio'
                ? t('admin.scheduledVideos.audioRecovery.retryAudioTitle', 'Rigenera Audio + Video')
                : t('admin.scheduledVideos.audioRecovery.regenTextTitle', 'Rigenera Testo')}
            </h3>

            <div className="sv-retry-detail">
              <p>
                <strong>Post #{recoveryTarget.post.id}</strong> — {recoveryTarget.post.user_email || '—'}
              </p>
              {recoveryTarget.post.status_message && (
                <div className="sv-retry-error-box">
                  <span className="sv-retry-label">{t('admin.scheduledVideos.previousError')}:</span>
                  <p className="sv-retry-error-text">{recoveryTarget.post.status_message}</p>
                </div>
              )}
            </div>

            <p className="sv-retry-warning">
              ⚠️{' '}
              {recoveryTarget.type === 'audio'
                ? t(
                    'admin.scheduledVideos.audioRecovery.retryAudioNote',
                    'Verranno rigenerati audio e video usando lo script attuale (il testo non cambia).'
                  )
                : t(
                    'admin.scheduledVideos.audioRecovery.regenTextNote',
                    'Lo script verrà riscritto dall\'AI e il post tornerà in bozza: poi rigenera l\'audio.'
                  )}
            </p>

            <div className="sv-retry-actions">
              <button
                className="sv-btn-retry-confirm"
                onClick={confirmRecovery}
                disabled={recoveryRunning}
              >
                {recoveryRunning
                  ? '⏳ ...'
                  : recoveryTarget.type === 'audio'
                  ? `🔄 ${t('admin.scheduledVideos.audioRecovery.retryAudioBtn', 'Rigenera Audio+Video')}`
                  : `✏️ ${t('admin.scheduledVideos.audioRecovery.regenTextBtn', 'Rigenera Testo')}`}
              </button>
              <button
                className="sv-btn-retry-cancel"
                onClick={cancelRecovery}
                disabled={recoveryRunning}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduledVideos;
